import { Action, createReducer, on } from '@ngrx/store';
import { fareAdapter, initialPricingApiState, reshopFareAdapter } from './pricing-api.state';
import {
  getFare,
  getFareComplete,
  getFareQuote,
  getFareQuoteComplete,
  initializePricingApiState,
  clearPricingState,
  getReshopFare,
  getReshopFareComplete,
  getReshopQuoteFare,
  getReshopQuoteFareComplete,
} from './pricing-api.actions';
import { Status } from '../../../models/status';
import { FareRequest } from '../../../dtos/request/pricing-api/fare-request';
import { ReshopFareRequest } from '../../../dtos/request/pricing-api/reshop-fare-request';
import { ReshopQuoteFareRequest } from '../../../dtos/request/pricing-api/reshop-quote-fare-request';

const featureReducer = createReducer(
  initialPricingApiState,
  on(initializePricingApiState, (state) => ({ ...state, ...initialPricingApiState })),
  on(getFare, (state, { request }) => ({
    ...state,
    fareResponses: fareAdapter.setOne(
      {
        id: buildQpxcIdString(request),
        crudStatus: Status.LOADING,
      },
      state.fareResponses
    ),
  })),
  on(getReshopQuoteFare, (state, { request }) => ({
    ...state,
    reshopFareResponses: reshopFareAdapter.setOne(
      {
        id: buildReshopQuoteIdString(request),
        crudStatus: Status.LOADING,
      },
      state.reshopFareResponses
    ),
  })),
  on(getReshopQuoteFareComplete, (state, { request, response }) => ({
    ...state,
    reshopFareResponses: reshopFareAdapter.updateOne(
      {
        id: buildReshopQuoteIdString(request),
        changes: {
          success: response.success,
          response: response.response,
          exceptionContent: response.exceptionContent,
          crudStatus: Status.STABLE,
        },
      },
      state.reshopFareResponses
    ),
  })),
  on(getFareComplete, (state, { request, response }) => ({
    ...state,
    fareResponses: fareAdapter.updateOne(
      {
        id: buildQpxcIdString(request),
        changes: {
          success: response.success,
          response: response.response,
          exceptionContent: response.exceptionContent,
          crudStatus: Status.STABLE,
        },
      },
      state.fareResponses
    ),
  })),
  on(getReshopFare, (state, { request }) => ({
    ...state,
    reshopFareResponses: reshopFareAdapter.setOne(
      {
        id: buildReshopIdString(request),
        crudStatus: Status.LOADING,
      },
      state.reshopFareResponses
    ),
  })),
  on(getReshopFareComplete, (state, { request, response }) => ({
    ...state,
    reshopFareResponses: reshopFareAdapter.updateOne(
      {
        id: buildReshopIdString(request),
        changes: {
          success: response.success,
          response: response.response,
          exceptionContent: response.exceptionContent,
          crudStatus: Status.STABLE,
        },
      },
      state.reshopFareResponses
    ),
  })),
  on(clearPricingState, (state) => ({
    ...state,
    ...initialPricingApiState,
  })),
  on(getFareQuote, (state) => ({
    ...state,
    fareQuoteResponse: {
      success: false,
      crudStatus: Status.LOADING,
    },
  })),
  on(getFareQuoteComplete, (state, { response }) => ({
    ...state,
    fareQuoteResponse: {
      success: response.success,
      response: response.response,
      exceptionContent: response.exceptionContent,
      crudStatus: Status.STABLE,
    },
  }))
);

function buildQpxcIdString(request: FareRequest): string {
  return `${request.sessionId}_${request.solutionSetId}_${request.solutionId}`;
}

function buildReshopIdString(request: ReshopFareRequest): string {
  return `${request.sessionId}_${request.solutionSetId}_${request.recordLocator}`;
}

export function pricingApiReducer(state = initialPricingApiState, action: Action) {
  return featureReducer(state, action);
}

function buildReshopQuoteIdString(request: ReshopQuoteFareRequest): string {
  const slicesKey = request.slices?.map((s) => `${s.origin}-${s.destination}`).join('_') || 'no-slices';
  const passengersKey = request.passengers?.length || 0;
  return `reshopQuote_${slicesKey}_${passengersKey}`;
}
